import { loadJSON } from '@antiopix/shared-ui/js/data-loader.js';
import { initCanvasFx } from '@antiopix/shared-ui/js/canvas-fx.js';

/* ─── Service SVG Icons ───────────────────────────────────── */
const SERVICE_ICONS = {
  web: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" fill="currentColor"/>
  </svg>`,
  mobile: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14zm-4-1H11v-2h2v2z" fill="currentColor"/>
  </svg>`,
  design: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 9 17.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" fill="currentColor"/>
  </svg>`,
  cloud: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" fill="currentColor"/>
  </svg>`
};

/* ─── getServicesHTML ─────────────────────────────────────── */
export function getServicesHTML(services = []) {
  // If no services passed (e.g. called from services page), use defaults
  const defaultServices = [
    {
      icon: 'web', title: 'Web Development', accent: '#f59e0b',
      description: 'High-performance web applications built for scale and conversion.',
      features: ['Custom Web Applications', 'E-Commerce Platforms', 'CMS & API Integration', 'SEO-Optimized Builds']
    },
    {
      icon: 'mobile', title: 'Mobile App Development', accent: '#4A00E0',
      description: 'Native and cross-platform apps that delight users on every device.',
      features: ['Android & iOS Development', 'React Native & Flutter', 'App Store Deployment', 'Push Notifications & APIs']
    },
    {
      icon: 'design', title: 'UI/UX Design', accent: '#06b6d4',
      description: 'Behaviorally-driven interfaces that captivate and convert.',
      features: ['User Research & Wireframes', 'Interactive Prototyping', 'Design Systems', 'Responsive UI Design']
    },
    {
      icon: 'cloud', title: 'Cloud & DevOps', accent: '#8B5CF6',
      description: 'Robust cloud infrastructure built to scale with your business.',
      features: ['Cloud Architecture (AWS/GCP)', 'Docker & Kubernetes', 'CI/CD Pipelines', 'Performance Monitoring']
    }
  ];

  const items = services.length ? services : defaultServices;

  const cardsHTML = items.map((svc, i) => {
    const accentHex = svc.accent || '#8B5CF6';
    const iconSVG = SERVICE_ICONS[svc.icon] || SERVICE_ICONS.web;
    const featuresHTML = (svc.features || []).map(f => `<li>${f}</li>`).join('');

    return `
      <div class="service-card-v2 animate-on-scroll"
           id="service-card-${i}"
           style="--card-accent:${accentHex}; --card-glow:${accentHex}33; --card-icon-bg:${accentHex}1a; animation-delay:${i * 0.1}s;">
        <div class="service-card-icon" style="color:${accentHex};">
          ${iconSVG}
        </div>
        <h3>${svc.title}</h3>
        <p>${svc.description}</p>
        <ul class="service-features">${featuresHTML}</ul>
        <a href="/#contactUs" class="service-card-link">
          Get Started ↗
        </a>
      </div>
    `;
  }).join('');

  return `
    <div class="section-header animate-on-scroll">
      <span class="section-eyebrow">What We Do</span>
      <h2 class="text-hero-display" style="font-size: clamp(2rem, 4vw, 3.25rem); margin-bottom: 1rem;">Our Services</h2>
      <p style="font-size: 1.1rem; color: var(--text-secondary); max-width: 560px; margin: 0 auto;">
        End-to-end digital solutions built to grow your business and delight your users.
      </p>
    </div>
    <div class="grid" style="gap:1.75rem; grid-template-columns: repeat(auto-fill, minmax(280px,1fr));">
      ${cardsHTML}
    </div>
    <div style="text-align:center; margin-top:2.5rem; display:none;">
      <a href="/#contactUs" class="btn btn-outline">View All Services ↗</a>
    </div>
  `;
}

/* ─── Services Page ───────────────────────────────────────── */
export async function renderPage() {
  const data = await loadJSON('/data/services.json');
  if (!data) return;

  const content = document.getElementById('page-content');

  content.innerHTML = `
    <section class="hero" id="services-hero" style="position:relative; overflow:hidden; min-height:100vh; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center;">
      <div class="animate-on-scroll" style="position:relative; z-index:1;">
        <span class="section-eyebrow" style="margin-bottom:1.5rem; display:inline-block;">What We Offer</span>
        <h1 class="text-hero-display text-gradient" style="margin-bottom:1.5rem;">${data.hero.title}</h1>
        <p style="font-size:1.35rem; color:var(--text-secondary); max-width:580px; margin:0 auto 2.5rem auto; line-height:1.65;">
          End-to-end digital solutions — from strategy and design to development and launch.
        </p>
        <a href="/#contactUs" class="btn btn-primary glow-button" id="services-hero-cta">
          Start Your Project
        </a>
      </div>
      <div class="scroll-indicator">
        <span>Scroll</span>
        <div class="scroll-arrow">↓</div>
      </div>
    </section>

    <section class="section" id="services" style="position:relative; z-index:2; background:var(--bg-primary);">
      ${getServicesHTML()}
    </section>
  `;

  initCanvasFx('services-hero');
}
